// src/components/kiosk/POSSection/Cart.jsx
import { formatCurrency } from '../../../utils/formatters';
import { ShoppingCart, Trash2, Plus, Minus, User, Percent, Home, Users, ClipboardList, Pause, Play, X } from 'lucide-react';

function Cart({
  cart,
  customerDetails,
  discount,
  applyVAT,
  heldCarts = [],
  calculateSubtotal,
  calculateVAT,
  calculateTotal,
  onUpdateQty,
  onRemoveItem,
  onClearCart,
  onHoldCart,
  onRecallCart,
  onDeleteHeldCart,
  onOpenCustomer,
  onRemoveCustomer,
  onSetDiscount,
  onSetApplyVAT,
  onCheckout,
  onHomeClick,
  onCustomersClick,
  onOrdersClick,
  onBackToItems
}) {
  const subtotal = calculateSubtotal();
  const vat = calculateVAT();
  const total = calculateTotal();

  return (
    <div
      className="w-full flex flex-col h-full"
      style={{
        padding: '1rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        borderLeft: '1px solid rgba(59, 130, 246, 0.2)',
        overflowY: 'hidden',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        fontFamily: 'Inter, system-ui, sans-serif'
      }}
    >
      {/* Mobile: back-to-items button */}
      <button
        onClick={onBackToItems}
        className="md:hidden flex items-center gap-2 w-full px-3 py-2 mb-2 text-sm font-semibold text-blue-600 bg-blue-50 rounded-lg"
        style={{ border: 'none', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}
      >
        ← Back to Items
      </button>
      {/* Compact Header with Icon Menu */}
      <div
        style={{
          padding: '0.75rem',
          borderBottom: '1px solid #e5e7eb',
          background: '#f9fafb'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ShoppingCart size={20} style={{ color: '#3b82f6' }} />
            <h2 style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937', margin: 0 }}>
              Cart
            </h2>
          </div>
          {cart.length > 0 && (
            <button
              onClick={onClearCart}
              style={{
                padding: '0.25rem',
                border: 'none',
                borderRadius: '0.375rem',
                background: '#fee2e2',
                color: '#dc2626',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>

        {/* Compact Icon Buttons */}
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <button
            onClick={onHomeClick}
            style={{
              flex: 1,
              padding: '0.4rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.375rem',
              background: '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.125rem'
            }}
            title="Go to home (will prompt if cart has items)"
          >
            <Home size={14} style={{ color: '#6b7280' }} />
            <span style={{ fontSize: '0.625rem', color: '#6b7280', fontWeight: '500' }}>Home</span>
          </button>

          <button
            onClick={onCustomersClick}
            style={{
              flex: 1,
              padding: '0.4rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.375rem',
              background: '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.125rem'
            }}
          >
            <Users size={14} style={{ color: '#6b7280' }} />
            <span style={{ fontSize: '0.625rem', color: '#6b7280', fontWeight: '500' }}>Customer</span>
          </button>

          <button
            onClick={onOrdersClick}
            style={{
              flex: 1,
              padding: '0.4rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.375rem',
              background: '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.125rem'
            }}
          >
            <ClipboardList size={14} style={{ color: '#6b7280' }} />
            <span style={{ fontSize: '0.625rem', color: '#6b7280', fontWeight: '500' }}>Orders</span>
          </button>
        </div>
      </div>

      {/* Cart Items - LARGER, NO SCROLL CONSTRAINTS */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem' }}>
        {cart.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <ShoppingCart size={48} style={{ color: '#d1d5db', marginBottom: '0.75rem' }} />
            <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', margin: 0 }}>Cart is empty</p>
            <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>Add items to start</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {cart.map(item => (
              <CartItem
                key={item._id}
                item={item}
                onUpdateQty={onUpdateQty}
                onRemove={onRemoveItem}
              />
            ))}
          </div>
        )}
      </div>

      {/* Customer, Discount, VAT & Totals */}
      {cart.length > 0 && (
        <div style={{ padding: '0.75rem', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
          {/* Customer Button - Compact with Delete */}
          <div style={{ marginBottom: '0.5rem' }}>
            {customerDetails.name ? (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.25rem',
                padding: '0.5rem 0.75rem',
                border: '1px solid #10b981',
                borderRadius: '0.375rem',
                background: '#d1fae5'
              }}>
                <User size={14} style={{ color: '#10b981' }} />
                <span style={{ flex: 1, fontSize: '0.75rem', fontWeight: '600', color: '#065f46' }}>
                  {customerDetails.name}
                </span>
                <button
                  onClick={onOpenCustomer}
                  style={{
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.625rem',
                    border: 'none',
                    borderRadius: '0.25rem',
                    background: 'rgba(16, 185, 129, 0.2)',
                    color: '#065f46',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={onRemoveCustomer}
                  style={{
                    padding: '0.125rem',
                    border: 'none',
                    background: 'transparent',
                    color: '#dc2626',
                    cursor: 'pointer'
                  }}
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                onClick={onOpenCustomer}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.375rem',
                  background: '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontFamily: 'Inter, sans-serif'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <User size={14} style={{ color: '#3b82f6' }} />
                  <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#1f2937' }}>
                    Add Customer
                  </span>
                </div>
                <span style={{ fontSize: '0.625rem', color: '#9ca3af' }}>
                  Optional
                </span>
              </button>
            )}
          </div>

          {/* Discount - Smaller */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.5rem' }}>
            <Percent size={14} style={{ color: '#10b981' }} />
            <input
              type="number"
              min="0"
              max="100"
              value={discount}
              onChange={(e) => onSetDiscount(Number(e.target.value))}
              placeholder="Discount %"
              style={{
                flex: 1,
                padding: '0.5rem',
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                fontSize: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}
            />
          </div>

          {/* VAT Toggle - Smaller */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.5rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.375rem',
              background: '#ffffff',
              cursor: 'pointer',
              marginBottom: '0.5rem'
            }}
          >
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#1f2937' }}>VAT (7.5%)</span>
            <input
              type="checkbox"
              checked={applyVAT}
              onChange={(e) => onSetApplyVAT(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
          </label>

          {/* Totals - Compact */}
          <div style={{ padding: '0.75rem', background: '#ffffff', borderRadius: '0.375rem', marginBottom: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
              <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Subtotal:</span>
              <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#1f2937' }}>{formatCurrency(subtotal)}</span>
            </div>

            {applyVAT && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>VAT:</span>
                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#1f2937' }}>{formatCurrency(vat)}</span>
              </div>
            )}

            {discount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#10b981' }}>Discount ({discount}%):</span>
                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#10b981' }}>
                  -{formatCurrency((subtotal + vat) * (discount / 100))}
                </span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: '2px solid #e5e7eb' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: '700', color: '#1f2937' }}>Total:</span>
              <span style={{ fontSize: '1rem', fontWeight: '700', color: '#3b82f6' }}>{formatCurrency(total)}</span>
            </div>
          </div>

          {/* Hold and Checkout Buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <button
              onClick={onHoldCart}
              style={{
                flex: 1,
                padding: '0.75rem',
                border: 'none',
                borderRadius: '0.375rem',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: '#ffffff',
                fontSize: '0.875rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontFamily: 'Inter, sans-serif',
                boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem'
              }}
            >
              <Pause size={14} />
              Hold
            </button>
            <button
              onClick={onCheckout}
              style={{
                flex: 2,
                padding: '0.75rem',
                border: 'none',
                borderRadius: '0.375rem',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: '#ffffff',
                fontSize: '0.875rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontFamily: 'Inter, sans-serif',
                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
              }}
            >
              Checkout
            </button>
          </div>

          
        </div>
      )}
    </div>
  );
}

function CartItem({ item, onUpdateQty, onRemove }) {
  return (
    <div
      style={{
        padding: '0.75rem',
        border: '1px solid #e5e7eb',
        borderRadius: '0.375rem',
        background: '#ffffff'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <div style={{ flex: 1 }}>
          <h4 style={{ fontSize: '0.8125rem', fontWeight: '600', color: '#1f2937', margin: '0 0 0.125rem 0' }}>
            {item.name}
          </h4>
          <p style={{ fontSize: '0.6875rem', color: '#6b7280', margin: 0 }}>
            {formatCurrency(item.price)} each
          </p>
        </div>
        <button
          onClick={() => onRemove(item._id)}
          style={{
            padding: '0.25rem',
            border: 'none',
            background: 'transparent',
            color: '#dc2626',
            cursor: 'pointer'
          }}
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <button
            onClick={() => onUpdateQty(item._id, item.cartQty - 1)}
            style={{
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #e5e7eb',
              borderRadius: '0.25rem',
              background: '#ffffff',
              cursor: 'pointer'
            }}
          >
            <Minus size={12} />
          </button>
          <span style={{ width: '32px', textAlign: 'center', fontWeight: '600', fontSize: '0.8125rem' }}>
            {item.cartQty}
          </span>
          <button
            onClick={() => onUpdateQty(item._id, item.cartQty + 1)}
            style={{
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #e5e7eb',
              borderRadius: '0.25rem',
              background: '#ffffff',
              cursor: 'pointer'
            }}
          >
            <Plus size={12} />
          </button>
        </div>
        <span style={{ fontSize: '0.9375rem', fontWeight: '700', color: '#1f2937' }}>
          {formatCurrency(item.price * item.cartQty)}
        </span>
      </div>
    </div>
  );
}

export default Cart;